import type React from "react";
import { runWhenIdle } from "./performance";

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

export const META_PIXEL_ID = "2545728412542082";
const PRODUCT_ID = "50606257078497";

export function initMetaPixel() {
  if (typeof window === "undefined" || window.fbq) return;

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  // Queue init + PageView immediately (cheap). Events fired before the library
  // loads (e.g. AddToCart) are queued and sent as soon as it arrives.
  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");

  // Load the Meta library after the page has loaded, so it doesn't compete with rendering.
  runWhenIdle(loadPixelScript);
}

let scriptRequested = false;

function loadPixelScript() {
  if (scriptRequested) return;
  scriptRequested = true;
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const first = document.getElementsByTagName("script")[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
}

export function trackAddToCart() {
  window.fbq?.("track", "AddToCart", {
    content_ids: [PRODUCT_ID],
    content_name: "De cero a tu empresa de limpieza",
    content_type: "product",
  });
}

/**
 * Click handler for checkout links: fires AddToCart, then navigates after a short
 * delay so the event has time to be sent before the page changes.
 */
export function handleCheckoutClick(event: React.MouseEvent<HTMLAnchorElement>) {
  // Make sure the library is requested if the user clicks before it loaded.
  loadPixelScript();
  trackAddToCart();
  const opensNewTab =
    event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0 || event.currentTarget.target === "_blank";
  if (opensNewTab) return;
  event.preventDefault();
  const href = event.currentTarget.href;
  window.setTimeout(() => {
    window.location.href = href;
  }, 300);
}
